import type { UserAvailabilityModal } from '../types/interfaces'

import { faArrowRightFromBracket, faArrowRightToBracket } from '@fortawesome/pro-regular-svg-icons'

import { useProfileStore } from '@entities/viewer'

import { AvailabilityStatus } from '../types/enums'
import { useAvailabilityUpdate } from './availability.hook'

const MODAL_CHECK_IN: UserAvailabilityModal = {
  title: 'Check-in',
  detail: `Do you want to check-in and set your status as <strong class="user-availability__status user-availability__status--available">${AvailabilityStatus.Available}</strong>?`,
  mode: 'approve',
  icon: faArrowRightToBracket,
  iconColor: 'emerald-600',
}

const MODAL_CHECK_OUT: UserAvailabilityModal = {
  title: 'Check-out',
  detail: `Do you want to check-out and set your status as <strong class="user-availability__status user-availability__status--unavailable">${AvailabilityStatus.Unavailable}</strong>?`,
  mode: 'delete',
  icon: faArrowRightFromBracket,
  iconColor: 'red-600',
}

interface UserAvailabilityReturn {
  isAvailable: ComputedRef<boolean>
  statusText: ComputedRef<AvailabilityStatus>
  modal: ComputedRef<UserAvailabilityModal>
  isModalShown: Ref<boolean>
  isLoading: Ref<boolean>
  setModalShown: (value: boolean) => void
  setAvailability: () => Promise<void>
}

export const useUserAvailability = (): UserAvailabilityReturn => {
  const profileStore = useProfileStore()

  const isModalShown = ref<boolean>(false)
  const isLoading = ref<boolean>(false)

  const isAvailable = computed<boolean>(() => profileStore.isAvailable)
  const statusText = computed<AvailabilityStatus>(() => isAvailable.value ? AvailabilityStatus.Available : AvailabilityStatus.Unavailable)
  const modal = computed<UserAvailabilityModal>(() => isAvailable.value ? MODAL_CHECK_OUT : MODAL_CHECK_IN)

  const setModalShown = (value: boolean): void => {
    if (isLoading.value) return
    isModalShown.value = value
  }

  const setAvailability = async (): Promise<void> => {
    if (isLoading.value) return

    isLoading.value = true
    await useAvailabilityUpdate(!isAvailable.value)
    isLoading.value = false
    isModalShown.value = false
  }

  return {
    isAvailable,
    statusText,
    modal,
    isModalShown,
    isLoading,
    setModalShown,
    setAvailability,
  }
}
